import { useProperty, useTranslation } from '@eti/providers';
import { css } from '@eti/styles';
import { breakpoints, vars } from '@etg/wings';
import { Helmet } from 'react-helmet';
import { Collection, DevBar } from '@eti/debug';
import { ConditionalWrapper, Panel } from '@eti/components';
import { useMediaQuery } from 'usehooks-ts';
import { useMemo } from 'react';
import type { TabItem } from '../../../widgets/tabs/components/RoutedTabs';
import RoutedTabs from '../../../widgets/tabs/components/RoutedTabs';
import { routes } from '../../../constants/routesAndApisConstants';
import { useOrderDetails } from '../containers/OrderDetailsContext';
import { DebugCommonComponents } from '../../../common/debug/components';
import { ChatProvider } from '../../../common/chat/ChatContext';
import { isDev } from '../../../common/debug/utils';
import { getDestinationBackgroundImage } from '../../../utils/destinationBackgroundImage';
import OrderDetailsManageTrip from './manage-trip/OrderDetailsManageTrip';
import OrderDetailsTravelInformation from './travel-information/OrderDetailsTravelInformation';
import OrderDetailsCheckInV1 from './check-in/v1/OrderDetailsCheckIn';
import OrderDetailsCheckIn from './check-in/OrderDetailsCheckIn';
import OrderDetailsOverview from './overview/OrderDetailsOverview';
import OrderDetailsExtraProducts from './extra-products/OrderDetailsExtraProducts';
import OrderDetailsContactUs from './contact-us/OrderDetailsContactUs';

const panelStyles = css`
  height: 360px;
  position: absolute;
  top: 54px; /* tabs height */
  width: 100%;

  ::after {
    background: linear-gradient(to bottom, transparent 80%, ${vars.colors.pageBackdrop} 100%);
    content: '';
    height: 100%;
    position: absolute;
    top: 0;
    width: 100%;
  }
`;

const sectionStyles = css`
  background-color: ${vars.colors.pageBackdrop};
  height: 100%;
  min-height: 414px; /*  tabs height + background image height - necessary for loading state */
  position: relative;
`;

const OrderDetails = () => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { bounds, shouldShowManageTrip, isOrderActive, customerDetails } = useOrderDetails();

  const shouldShowTravelInformationTab = p('OrderDetails.TravelInformation.Enabled');
  const isCheckInV2Enabled = p('OrderDetails.CheckIn.V2.Enabled');

  const isChatEnabled =
    p('Chat.WebMessages.Enabled') || p('Chat.Widget.Enabled') || p('Chatbot.Oriana.Enabled');

  const tabItems: TabItem[] = [
    {
      heading: { dataTestId: 'tab-overview', text: t('OrderDetails.Tab.Overview.Description') },
      content: <OrderDetailsOverview />,
      route: { exact: true, path: routes.ORDER_DETAILS },
      snowplowLabel: 'Overview',
    },
    ...(shouldShowManageTrip
      ? [
          {
            heading: {
              dataTestId: 'tab-manage-trip',
              text: t('OrderDetails.Tab.ManageTrip.Description'),
            },
            content: <OrderDetailsManageTrip />,
            route: { path: routes.ORDER_DETAILS_MANAGE_TRIP },
            snowplowLabel: 'Manage Trip',
          },
        ]
      : []),
    ...(isOrderActive
      ? [
          {
            heading: {
              dataTestId: 'tab-check-in',
              text: t('OrderDetails.Tab.CheckIn.Description'),
            },
            content: isCheckInV2Enabled ? <OrderDetailsCheckIn /> : <OrderDetailsCheckInV1 />,
            route: { path: routes.ORDER_DETAILS_CHECK_IN },
            snowplowLabel: 'Check In',
          },
        ]
      : []),
    ...(shouldShowTravelInformationTab
      ? [
          {
            heading: {
              dataTestId: 'tab-travel-information',
              text: t('OrderDetails.Tab.TravelInformation.Description'),
            },
            content: <OrderDetailsTravelInformation />,
            route: { path: routes.ORDER_DETAILS_TRAVEL_INFORMATION },
            snowplowLabel: 'Travel Information',
          },
        ]
      : []),
    ...(isOrderActive
      ? [
          {
            heading: {
              dataTestId: 'tab-extra-products',
              text: t('Receipt.ExtraProducts.Heading'),
            },
            content: <OrderDetailsExtraProducts />,
            route: { path: routes.ORDER_DETAILS_EXTRA_PRODUCTS },
            snowplowLabel: 'Extra Products',
          },
        ]
      : []),

    {
      heading: {
        dataTestId: 'tab-contact-us',
        text: t('OrderDetails.Tab.ContactUs.Description'),
      },
      content: <OrderDetailsContactUs />,
      route: { path: routes.ORDER_DETAILS_CONTACT_US },
      snowplowLabel: 'Contact Us',
    },
  ];

  const isWiderScreen = useMediaQuery(`(min-width: ${breakpoints._768})`);
  const destinationImage = useMemo(() => {
    const lastSegment = bounds[0]?.segments[bounds[0].segments.length - 1];
    if (lastSegment?.__typename === 'TripSegment') {
      return getDestinationBackgroundImage(lastSegment.destination.cityCode);
    }
    return null;
  }, [bounds]);

  return (
    <>
      <Helmet title={t('PageTitle.rf.order-details.action')} />
      <ConditionalWrapper
        condition={isChatEnabled}
        wrapper={(children) => (
          <ChatProvider supportedChatTypes={['oriana', 'genesys']} userName={customerDetails.name}>
            {children}
          </ChatProvider>
        )}
      >
        <section className={sectionStyles} data-testid="order-details-page">
          {isWiderScreen && (
            <Panel
              backgroundImage={destinationImage}
              className={panelStyles}
              dataTestId="header-background-image"
              defaultBackgroundImage="content/fallback-destination"
            />
          )}
          <RoutedTabs tabItems={tabItems} tabNotFoundFallbackUrl={routes.ORDER_DETAILS} />
        </section>
      </ConditionalWrapper>
      {isDev && (
        <DevBar>
          <Collection>
            <DebugCommonComponents />
          </Collection>
        </DevBar>
      )}
    </>
  );
};

export default OrderDetails;
